// Care+ Package teaser block — placed on the landing page just before the
// Spin Wheel section. Drives traffic into the AMC flow at /me.
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { trackViewContent } from "@/lib/pixel";

export default function CarePlusSection() {
  const [pkg, setPkg] = useState(null);
  const sectionRef = useRef(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    api.get("/customer/care-plus")
      .then(({ data }) => setPkg(data))
      .catch(() => { /* silent */ });
  }, []);

  // Fire ViewContent when Care+ section becomes visible (35% in viewport)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            trackViewContent({ source: "landing_section" });
            obs.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const coupons = pkg?.coupons || [];

  return (
    <section
      id="care-plus"
      ref={sectionRef}
      data-testid="care-plus-section"
      className="bg-[#0A0A0A] text-white px-5 lg:px-8 py-16 sm:py-24"
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-3">
            Members Only
          </p>
          <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            AutoHQ <span className="text-[#D4AF37]">Care+</span>
            <br />
            <span className="text-white/80 text-2xl sm:text-3xl font-normal">
              ₹10,000 of car care, yours for ₹1,249/year.
            </span>
          </h2>
          <p className="mt-6 text-base text-white/70 max-w-lg leading-relaxed">
            One annual membership. Up to 2 cars under your name. 20+ redemptions across washes, AC, alignment, denting, and full services. 100% genuine parts on every visit.
          </p>

          <div className="mt-8 flex items-end gap-4">
            <p className="text-5xl font-extrabold">
              ₹{pkg?.price_rupees?.toLocaleString("en-IN") || "1,249"}
              <span className="text-base font-normal text-white/60"> /year</span>
            </p>
            <p className="text-sm text-white/60 line-through pb-2">₹10,000</p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button asChild
              data-testid="care-plus-buy-cta"
              className="bg-[#D4AF37] hover:bg-[#bf9f33] text-[#0A0A0A] font-bold text-base px-8 py-6 rounded-sm"
            >
              <a href="/autohq-care-plus">Buy Care+ Now →</a>
            </Button>
            <Button asChild variant="outline"
              data-testid="care-plus-login-cta"
              className="border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent font-semibold text-base px-8 py-6 rounded-sm"
            >
              <a href="/me">Existing member? Log in</a>
            </Button>
          </div>

          <div className="mt-8 flex items-center gap-6 text-xs text-white/50 uppercase tracking-wider">
            <span>· 365 days validity</span>
            <span>· 2 cars covered</span>
            <span>· 20+ redemptions</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="bg-white/[0.04] border border-white/10 rounded-sm p-6 sm:p-8 backdrop-blur"
          data-testid="care-plus-coupon-list"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-5">
            What's inside the package
          </p>
          <ul className="space-y-3.5">
            {coupons.map((c, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <span className="text-white/90">{c.label}</span>
                  {c.uses > 1 && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-[#D4AF37] bg-[#D4AF37]/10 rounded-sm px-1.5 py-0.5">
                      ×{c.uses}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
